import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import ConfirmActionDialog from '../workspace/ConfirmActionDialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '../ui/sheet';

export default function EntityEditDrawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  onSave,
  saving = false,
  error = null,
  saveLabel = 'Save changes',
  hasChanges = false,
  changedCount = null,
  saveDisabled = false,
  onDiscard
}) {
  const [discardOpen, setDiscardOpen] = useState(false);

  const requestOpenChange = (nextOpen) => {
    if (!nextOpen && hasChanges && !saving) {
      setDiscardOpen(true);
      return;
    }
    onOpenChange(nextOpen);
  };

  const discardChanges = () => {
    onDiscard?.();
    setDiscardOpen(false);
    onOpenChange(false);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={requestOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-2xl">
        <SheetHeader className="border-b border-slate-200 px-6 py-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <SheetTitle className="text-xl text-slate-950">{title}</SheetTitle>
              {description && (
                <SheetDescription className="text-sm text-slate-600">
                  {description}
                </SheetDescription>
              )}
            </div>
            {hasChanges && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800">
                {changedCount ? `${changedCount} unsaved` : 'Unsaved changes'}
              </span>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {children}
        </div>

        <SheetFooter className="border-t border-slate-200 bg-slate-50 px-6 py-4">
          <Button variant="outline" onClick={() => requestOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving || saveDisabled}>
            {saving ? 'Saving...' : saveLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
    <ConfirmActionDialog
      open={discardOpen}
      onOpenChange={setDiscardOpen}
      tone="warning"
      eyebrow="Unsaved edit checkpoint"
      title="Leave without saving these edits?"
      description="You have draft changes in this drawer. Nothing has been committed to the record yet."
      summaryLabel="If you discard"
      items={[
        changedCount ? `${changedCount} changed field${changedCount === 1 ? '' : 's'} will be cleared.` : 'Unsaved drawer edits will be cleared.',
        'The saved record and activity trail will stay unchanged.',
        'Choose Keep editing to return and save the draft changes.'
      ]}
      cancelLabel="Keep editing"
      confirmLabel="Discard edits"
      destructive
      onConfirm={discardChanges}
    />
    </>
  );
}
